import { Injectable } from '@angular/core';
import { CallProvider } from '../common/interfaces/rtc-interface';
import { FLAGS, FlagsService } from './flags.service';
import { DeviceAndBrowserDetectorService } from './device-and-browser-detector.service';
import { LocalStorageService, LocalStorageItemIDs } from './local-storage.service';

const DEFAULT_CONFIG = {
  livekit: {
    mobile: true,
    tablet: true,
    desktop: true,
  },
  daily: {
    mobile: true,
    tablet: true,
    desktop: true,
  },
};

type ConfigType = typeof DEFAULT_CONFIG;

@Injectable({
  providedIn: 'root',
})
export class NoiseCancellationConfigurationService {
  constructor(
    private flagsService: FlagsService,
    private deviceAndBrowserDetectorService: DeviceAndBrowserDetectorService,
    private localStorageService: LocalStorageService,
  ) {}

  isEnabled(provider: CallProvider): boolean {
    const config: ConfigType =
      (this.flagsService.featureFlagsVariables?.enable_noise_cancellation
        ?.noise_cancellation_config as any as ConfigType | undefined) || DEFAULT_CONFIG;
    const providerConfig = config[provider];
    let deviceEnabled: boolean;
    if (this.deviceAndBrowserDetectorService.isMobile()) {
      deviceEnabled = providerConfig.mobile;
    } else if (this.deviceAndBrowserDetectorService.isTabletDevice()) {
      deviceEnabled = providerConfig.tablet;
    } else {
      deviceEnabled = providerConfig.desktop;
    }

    return deviceEnabled;
  }

  /**
   * Gets the noise cancellation state based on the flags and local storage
   * If allow_noise_cancellation_local_state flag is enabled, we use local storage value if available
   * Otherwise we fall back to the default state flag
   */
  getNoiseCancellationState(): boolean {
    const useLocalState = this.flagsService.isFlagEnabled(
      FLAGS.ALLOW_NOISE_CANCELLATION_LOCAL_STATE,
    );
    const defaultState = this.flagsService.isFlagEnabled(FLAGS.NOISE_CANCELLATION_DEFAULT_STATE);

    if (useLocalState) {
      const storedValue = this.localStorageService.getItem(
        LocalStorageItemIDs.NOISE_CANCELLATION_STATE,
      );
      if (storedValue) {
        return storedValue === 'true';
      }
    }

    return defaultState;
  }

  /**
   * Saves the noise cancellation state to local storage
   */
  saveNoiseCancellationState(isEnabled: boolean): void {
    this.localStorageService.setItem(
      LocalStorageItemIDs.NOISE_CANCELLATION_STATE,
      isEnabled.toString(),
    );
  }
}
